/**
 * TransactionSendCopyInvoiceModal
 *
 * Thin wrapper around SingleEmailModal that provides transaction-specific
 * props (merge data, invoice attachment fetching, send-copy logging).
 *
 * The exported interface is unchanged so page-level imports keep working.
 */

import { useCallback } from "react";
import {
  SingleEmailModal,
  type EmailAttachment,
} from "@/components/shared/single-email-modal";
import { useTransactionDocumentDownload } from "@/hooks/useTransactionDocumentDownload";
import { useLogSendCopyInvoice } from "@/hooks/useLogSendCopyInvoice";
import { useLogCustomerEmail } from "@/hooks/useLogCustomerEmail";
import { toast } from "sonner";
import type { Transaction } from "@/types/transaction";

// ─── Props (re-exported for backward compatibility) ──────────────────────────

export interface TransactionSendCopyInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selected transactions — must all belong to the same customer. */
  transactions: Transaction[];
  onSuccess?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function TransactionSendCopyInvoiceModal({
  open,
  onOpenChange,
  transactions,
  onSuccess,
}: Readonly<TransactionSendCopyInvoiceModalProps>) {
  const firstTx = transactions[0];
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const logSendCopyInvoice = useLogSendCopyInvoice();
  const logCustomerEmail = useLogCustomerEmail();

  // Derive customer info from the first transaction
  const customerId = firstTx?.customerId?.toString() ?? "";
  const customerName = firstTx?.customerName ?? "";
  const accountNumber = firstTx?.accountNumber ?? "";

  // Fetch invoice PDFs for all selected invoice-type transactions
  const handleFetchAttachments = useCallback(
    async (
      onProgress: (done: number, total: number) => void,
    ): Promise<EmailAttachment[]> => {
      const invoiceTxs = transactions.filter(
        (t) => t.transactionType === "invoice",
      );
      if (invoiceTxs.length === 0) return [];

      onProgress(0, invoiceTxs.length);

      const results = await Promise.allSettled(
        invoiceTxs.map(async (t) => {
          const doc = await downloadDocumentAsBase64(t.id);
          onProgress(
            invoiceTxs.indexOf(t) + 1,
            invoiceTxs.length,
          );
          return doc;
        }),
      );

      const successful = results
        .filter(
          (r): r is PromiseFulfilledResult<EmailAttachment> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);

      const failedCount = results.filter(
        (r) => r.status === "rejected",
      ).length;
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} invoice document(s) could not be downloaded`,
        );
      }

      return successful;
    },
    [transactions, downloadDocumentAsBase64],
  );

  // Log the send-copy-invoice action before the MFE step
  const handleBeforeSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
    }) => {
      logSendCopyInvoice.mutate({
        customerId: firstTx?.customerId,
        recipientEmail: payload.toEmails[0] ?? "",
        message: payload.subject,
        transactionIds: transactions.map((t) => t.id),
      });
    },
    [logSendCopyInvoice, firstTx, transactions],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logCustomerEmail.mutate({
        sourceId: customerId,
        toRecipients: payload.toEmails,
        ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
        subject: payload.subject,
        htmlBody: payload.htmlBody,
        templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
        templateName: payload.templateName,
        isSubjectModified: payload.isSubjectModified,
        isBodyModified: payload.isBodyModified,
        attachOutstandingInvoices: payload.attachOutstandingInvoices,
      });
    },
    [logCustomerEmail, customerId],
  );

  if (!firstTx) return null;

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Copy Invoice"
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="transaction"
      transactionIds={transactions.map((t) => t.id)}
      callbackEventName="creditcontrol:send-copy-invoice-done"
      attachLabel="Attach invoice copies"
      fetchAttachments={handleFetchAttachments}
      onBeforeSend={handleBeforeSend}
      onAfterSend={handleAfterSend}
      successMessage={`Copy invoice email sent for ${transactions.length} transaction(s)`}
      onSuccess={onSuccess}
      allowedCategoryTypes={["transactions", "general"]}
      allowedTemplateTypes={["email"]}
    />
  );
}
